import { useCallback, useEffect } from 'react';
import { difference, intersection } from 'lodash';
import { type ApolloClient } from '@apollo/client';
import { useMutation } from '@apollo/client/react';

import { useAgreementSuggestChangesSlice } from 'src/slices';
import { type IAgreementChangeParams, type IAgreement } from 'src/graphql/schema';
import { SuggestedChangeCreateDocument } from 'src/graphql/actions/suggestChanges/suggestedChangeCreate.mutation';

import { createRequiredContext } from '../createRequiredContext';

import type { ISuggestedChangeCreateMutation } from 'src/graphql/actions/suggestChanges/suggestedChangeCreate.mutation';

const setAgreementDataPayload = (newValues: string[], oldValues: string[]) => {
  const intersectionData = intersection(newValues, oldValues) || [];
  const differenceData = difference(newValues, oldValues) || [];
  let newData: string[] = [...intersectionData, ...differenceData];

  if (newData.length === oldValues.length) {
    newData = newData.every((item) => oldValues.includes(item)) ? [] : newData;
  }

  return newData;
};

const [useAgreementSuggestChanges, AgreementSuggestChangesProvider] = createRequiredContext<
  Omit<ReturnType<typeof useAgreementSuggestChangesSlice>, 'setInitialData'> & {
    sendSuggestedChanges: () => Promise<ApolloClient.MutateResult<ISuggestedChangeCreateMutation>>;
    isSendSuggestionsEnabled: boolean;
  }
>();

type Props = {
  escrowId: string;
  agreement: IAgreement;
  canCreateAgreementSuggestedChange: boolean;
};

const AgreementContextProvider = ({
  children,
  escrowId,
  agreement,
  canCreateAgreementSuggestedChange,
}: React.PropsWithChildren<Props>) => {
  // const {
  //   softwareEscrow: { id: escrowId, agreement, policy },
  // } = useSoftwareEscrow();
  const { state, setInitialData, ...actions } = useAgreementSuggestChangesSlice();
  useEffect(() => {
    const initialSettings = {
      context: agreement?.legalContext || [],
      otherContext: agreement?.legalContextOther || '',
      jurisdiction: agreement?.jurisdiction || undefined,
      liability: agreement?.liability || undefined,
      conditions: agreement?.conditions || [],
      conditionsOther: agreement?.conditionsOther || '',
    };
    setInitialData(initialSettings);
  }, []);

  const [createSuggestedChanges] = useMutation(SuggestedChangeCreateDocument);

  const sendSuggestedChanges = useCallback(async () => {
    const changes = {
      context: setAgreementDataPayload(state.data.context, agreement?.legalContext || []),
      otherContext: state.data.otherContext !== agreement?.legalContextOther ? state.data.otherContext : undefined,
      conditions: setAgreementDataPayload(state.data.conditions, agreement?.conditions || []),
      conditionsOther:
        state.data.conditionsOther !== agreement?.conditionsOther ? state.data.conditionsOther : undefined,
      jurisdiction: agreement?.jurisdiction !== state.data.jurisdiction ? state.data.jurisdiction : undefined,
      liability: agreement?.liability !== state.data.liability ? state.data.liability : undefined,
    };

    return createSuggestedChanges({
      variables: {
        escrowId,
        suggestedChangeParams: {
          agreementSuggestedChange: {
            ...(changes.context.length && { legalContext: changes.context }),
            ...(changes.otherContext && { legalContextOther: changes.otherContext }),
            ...(changes.conditions.length && { conditions: changes.conditions }),
            ...(changes.conditionsOther && { conditionsOther: changes.conditionsOther }),
            ...(changes.jurisdiction && {
              jurisdiction: changes.jurisdiction,
            }),
            ...(changes.liability && { liability: changes.liability }),
          } as IAgreementChangeParams,
        },
      },
    });
  }, [state.data]);

  const isSendSuggestionsEnabled =
    !Object.values(state.validation).some(({ isRequired }) => isRequired) &&
    Object.values(state.validation).some(({ hasChanges }) => hasChanges) &&
    canCreateAgreementSuggestedChange;

  const providerValue = {
    state,
    sendSuggestedChanges,
    isSendSuggestionsEnabled,
    ...actions,
  };

  return <AgreementSuggestChangesProvider value={providerValue}>{children}</AgreementSuggestChangesProvider>;
};

export { useAgreementSuggestChanges, AgreementContextProvider };
